import { ApolloError } from '@apollo/client';
import { CategoryNameLookup } from 'articleSuggestions/hooks/useCategoryNames';
import { Suggestion } from 'articleSuggestions/utils/toSuggestion';
import {
  AiDraftType,
  KnowledgeGapJudgeQuery,
  KnowledgeGapSkipReason,
} from 'lib-knowledge-management-common/graphql';
import { toSummary } from 'lib-knowledge-management-common/utils/toSummary';

export type JudgeOutcome = NonNullable<
  KnowledgeGapJudgeQuery['knowledgeGapJudge']
>;
export type JudgedOutcome = Extract<
  JudgeOutcome,
  { __typename: 'KnowledgeGapJudgeOutcome_Judged' }
>;
export type KnowledgeGap = JudgedOutcome['gaps'][number];
export type KnowledgeGapDraft = JudgedOutcome['drafts'][number];

export type TestDraft = {
  key: string;
  draft: KnowledgeGapDraft;
  collectionId: string | undefined;
  categoryId: string | undefined;
  gaps: KnowledgeGap[];
};

// As the backend writes them: one new article for every gap nothing covers,
// or else one update per partly covered article.
function gapsFor(
  draft: KnowledgeGapDraft,
  gaps: readonly KnowledgeGap[],
): KnowledgeGap[] {
  return draft.type === AiDraftType.ArticleUpdate
    ? gaps.filter(
        (gap) => !!gap.articleId && gap.articleId === draft.forArticleId,
      )
    : gaps.filter((gap) => gap.gapType === 'notCovered');
}

export function toTestDrafts({ drafts, gaps }: JudgedOutcome): {
  testDrafts: TestDraft[];
  otherGaps: KnowledgeGap[];
} {
  const testDrafts = drafts.map((draft, index) => ({
    key: `test-draft-${index}`,
    draft,
    collectionId: undefined,
    categoryId: undefined,
    gaps: gapsFor(draft, gaps),
  }));
  const covered = new Set(testDrafts.flatMap((draft) => draft.gaps));
  return {
    testDrafts,
    otherGaps: gaps.filter((gap) => !covered.has(gap)),
  };
}

export type TestConversation = { csid: number; isInitiating?: boolean };

/** The tested conversation, then the similar ones the judge read with it. */
export function toTestConversations(
  csid: number,
  outcome: JudgeOutcome,
): TestConversation[] {
  const similar =
    outcome.__typename === 'KnowledgeGapJudgeOutcome_Judged'
      ? outcome.similarConversationIds.filter((id) => id !== csid)
      : [];
  return [{ csid, isInitiating: true }, ...similar.map((id) => ({ csid: id }))];
}

export function toTestSuggestion(
  { key, draft, collectionId, categoryId }: TestDraft,
  {
    conversations,
    judgedAt,
    collectionNamesById,
    categoryName,
  }: {
    conversations: readonly TestConversation[];
    judgedAt: number;
    collectionNamesById: ReadonlyMap<string, string>;
    categoryName: CategoryNameLookup;
  },
): Suggestion {
  // An update reports where its article lives; a new article is filed here.
  const located = !!draft.collection;
  return {
    id: key,
    type: draft.type,
    internalOnly: draft.internalOnly,
    title: draft.title,
    htmlContent: draft.htmlContent,
    languageId: draft.languageId,
    summary: draft.summary?.trim() || toSummary(draft.htmlContent),
    ...(located
      ? {
          collectionId: draft.collection?.id,
          collectionName: draft.collection?.name,
          categoryId: draft.category?.id,
          categoryName: draft.category?.name,
        }
      : {
          collectionId,
          collectionName: collectionId
            ? collectionNamesById.get(collectionId)
            : undefined,
          categoryId,
          categoryName: categoryName(categoryId, draft.languageId),
        }),
    possibleCollectionIds: [],
    conversations,
    initiatedBy: undefined,
    activityAt: judgedAt,
    forArticleId: draft.forArticleId ?? undefined,
    isLocked: false,
  };
}

export const SKIP_REASON_TEXT: Record<KnowledgeGapSkipReason, string> = {
  [KnowledgeGapSkipReason.AlreadyDrafted]:
    'The AI has already written a suggestion for this issue.',
  [KnowledgeGapSkipReason.DetectionDisabled]:
    'AI article suggestions are turned off for your organization.',
  [KnowledgeGapSkipReason.JudgeInProgress]:
    'The AI is already checking this issue for gaps. Try again in a few minutes.',
  [KnowledgeGapSkipReason.NoProblemOrApproach]:
    'The AI could not tell what the customer’s problem was, or how it was handled.',
  [KnowledgeGapSkipReason.NoQueue]:
    'The conversation was not in a queue, and the AI only checks conversations from queues.',
  [KnowledgeGapSkipReason.QueueWithoutCollection]:
    'The conversation’s queue is not linked to a collection, so there is nowhere to check for gaps.',
};

export function skipReasonText(reason: KnowledgeGapSkipReason): string {
  return (
    SKIP_REASON_TEXT[reason] ??
    'The AI stopped before checking this conversation for gaps.'
  );
}

const GAP_TYPE_LABEL: Record<string, string> = {
  notCovered: 'Not covered',
  partiallyCovered: 'Partly covered',
};

export function gapTypeLabel(gapType: string): string {
  return GAP_TYPE_LABEL[gapType] ?? gapType;
}

export function isNotFound(error: unknown): boolean {
  return (
    error instanceof ApolloError &&
    error.graphQLErrors.some(
      ({ extensions }) => extensions?.code === 'NOT_FOUND',
    )
  );
}
